import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Megaphone, ExternalLink, Trash2, Plus, AlertCircle, Loader2, ShoppingBag, Sparkles, CheckCircle2 } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Seo from "@/components/Seo";

const PLAN_LABELS = {
  "7d":  { label: "7 jours",  desc: "Idéal pour vendre une carte rapidement" },
  "30d": { label: "30 jours", desc: "Le meilleur rapport visibilité/prix" },
  "90d": { label: "3 mois",   desc: "Pour les annonces longues ou les boutiques" },
};

function fmtDate(iso) {
  if (!iso) return "—";
  return new Date(iso).toLocaleDateString("fr-FR", { day: "2-digit", month: "short", year: "numeric" });
}

function StatusBadge({ status }) {
  const map = {
    active: { label: "Actif", color: "bg-emerald-100 text-emerald-700" },
    pending: { label: "En attente", color: "bg-amber-100 text-amber-700" },
    rejected: { label: "Refusé", color: "bg-rose-100 text-rose-700" },
    expired: { label: "Expiré", color: "bg-slate-100 text-slate-500" },
  };
  const m = map[status] || map.pending;
  return <span className={`text-[10px] font-bold uppercase tracking-wider px-2 py-0.5 rounded-md ${m.color}`}>{m.label}</span>;
}

export default function Sponsors() {
  const { user } = useAuth();
  const [active, setActive] = useState([]);
  const [mine, setMine] = useState([]);
  const [plans, setPlans] = useState({ free_beta: true, plans: [] });
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    title: "",
    description: "",
    url: "",
    image_url: "",
    price_eur: "",
    plan: "30d",
    card_id: "",
  });
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const [activeResp, plansResp, mineResp] = await Promise.all([
        api.get("/sponsors/active", { params: { limit: 30 } }),
        api.get("/sponsors/plans"),
        user?.email ? api.get("/sponsors/mine") : Promise.resolve({ data: [] }),
      ]);
      setActive(activeResp.data);
      setPlans(plansResp.data);
      setMine(mineResp.data);
    } catch {
      /* ignore */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [user?.email]);

  const submit = async (e) => {
    e.preventDefault();
    setError(null);
    if (!user?.email) {
      toast.error("Connectez-vous pour publier une annonce");
      return;
    }
    setSubmitting(true);
    try {
      await api.post("/sponsors", {
        title: form.title.trim(),
        description: form.description.trim() || null,
        url: form.url.trim(),
        image_url: form.image_url.trim() || null,
        price_eur: form.price_eur ? Number(form.price_eur) : null,
        plan: form.plan,
        card_id: form.card_id.trim() || null,
      });
      toast.success("Annonce publiée ✅");
      setShowForm(false);
      setForm({ title: "", description: "", url: "", image_url: "", price_eur: "", plan: "30d", card_id: "" });
      await load();
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setSubmitting(false);
    }
  };

  const deleteMine = async (id) => {
    if (!window.confirm("Supprimer cette annonce ?")) return;
    try {
      await api.delete(`/sponsors/${id}`);
      setMine((arr) => arr.filter((s) => s.id !== id));
      setActive((arr) => arr.filter((s) => s.id !== id));
      toast.success("Annonce supprimée");
    } catch (err) {
      toast.error(formatApiError(err));
    }
  };

  const handleClick = (s) => {
    api.post(`/sponsors/${s.id}/click`).catch(() => {});
  };

  const inputCls =
    "w-full px-3 py-2.5 text-sm rounded-xl bg-white border border-slate-200 text-slate-900 placeholder-slate-400 focus:border-[#E11D48] focus:outline-none";

  return (
    <div className="min-h-screen bg-white" data-testid="sponsors-page">
      <Seo title="Annonces sponsorisées" description="Mettez en avant vos cartes Pokémon à vendre sur Vinted, Cardmarket ou eBay." />

      <section className="max-w-5xl mx-auto px-6 lg:px-10 pt-12 pb-8">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <span className="text-[10px] tracking-[0.18em] text-slate-400 font-semibold">// MARKETPLACE</span>
            <h1 className="font-heading text-4xl sm:text-5xl font-extrabold tracking-tight text-slate-900 mt-2">
              Annonces sponsorisées
            </h1>
            <p className="text-sm text-slate-600 leading-relaxed mt-3 max-w-2xl">
              Vous vendez vos cartes sur Vinted, Cardmarket, eBay ou Mercari ? Mettez votre annonce en avant
              auprès de toute la communauté Cartonomie.
            </p>
          </div>
          {user?.email && (
            <button
              onClick={() => setShowForm((v) => !v)}
              data-testid="sponsor-new-btn"
              className="btn-primary inline-flex items-center gap-2"
            >
              <Plus className="w-4 h-4" />
              {showForm ? "Fermer" : "Publier mon annonce"}
            </button>
          )}
        </div>

        {plans.free_beta && (
          <div className="mt-6 p-4 rounded-2xl bg-emerald-50 border border-emerald-200 flex items-start gap-3" data-testid="sponsor-beta-badge">
            <Sparkles className="w-5 h-5 text-emerald-600 shrink-0 mt-0.5" />
            <div>
              <h3 className="font-heading font-bold text-sm text-emerald-900">Beta lancement — 100% gratuit</h3>
              <p className="text-xs text-emerald-800 mt-1">
                Pendant la période de lancement, publier votre annonce est <strong>totalement gratuit</strong>.
                Profitez-en pour mettre vos cartes en vente sans frais !
              </p>
            </div>
          </div>
        )}
      </section>

      {/* Submission form */}
      {showForm && (
        <section className="max-w-3xl mx-auto px-6 lg:px-10 pb-8" data-testid="sponsor-form-section">
          <div className="border border-slate-200 rounded-2xl p-6 shadow-sm">
            <h2 className="font-heading font-bold text-xl mb-1 text-slate-900">Publier une annonce</h2>
            <p className="text-xs text-slate-500 mb-5">
              Votre annonce apparaîtra sur la page Annonces et sur la fiche carte concernée.
            </p>

            <form onSubmit={submit} className="space-y-4">
              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Titre *</label>
                  <input type="text" required minLength={4} maxLength={120}
                    value={form.title} onChange={(e) => setForm({ ...form, title: e.target.value })}
                    placeholder="Charizard EX 100% Mint"
                    data-testid="sponsor-title-input" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">ID carte (optionnel)</label>
                  <input type="text" maxLength={80}
                    value={form.card_id} onChange={(e) => setForm({ ...form, card_id: e.target.value })}
                    placeholder="me4-3"
                    data-testid="sponsor-card-id-input" className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Lien de votre annonce *</label>
                <input type="url" required
                  value={form.url} onChange={(e) => setForm({ ...form, url: e.target.value })}
                  placeholder="https://www.vinted.fr/items/..."
                  data-testid="sponsor-url-input" className={inputCls} />
                <p className="text-[10px] text-slate-400 mt-1">Vinted, Cardmarket, eBay, Mercari, Leboncoin acceptés.</p>
              </div>

              <div className="grid sm:grid-cols-2 gap-4">
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Prix demandé (EUR)</label>
                  <input type="number" step="0.01" min="0" max="100000"
                    value={form.price_eur} onChange={(e) => setForm({ ...form, price_eur: e.target.value })}
                    placeholder="49.99"
                    data-testid="sponsor-price-input" className={inputCls} />
                </div>
                <div>
                  <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">URL image (optionnel)</label>
                  <input type="url"
                    value={form.image_url} onChange={(e) => setForm({ ...form, image_url: e.target.value })}
                    placeholder="https://..."
                    data-testid="sponsor-image-input" className={inputCls} />
                </div>
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Description</label>
                <textarea rows={3} maxLength={400}
                  value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })}
                  placeholder="État, langue, conditions de vente…"
                  data-testid="sponsor-desc-input" className={`${inputCls} resize-none`} />
              </div>

              <div>
                <label className="block text-[10px] font-bold uppercase tracking-widest text-slate-600 mb-2">Durée de mise en avant</label>
                <div className="grid sm:grid-cols-3 gap-2">
                  {(plans.plans || []).map((p) => (
                    <button type="button" key={p.id}
                      onClick={() => setForm({ ...form, plan: p.id })}
                      data-testid={`sponsor-plan-${p.id}`}
                      className={`text-left p-3 rounded-xl border transition ${
                        form.plan === p.id ? "border-slate-900 bg-slate-50" : "border-slate-200 hover:border-slate-400"
                      }`}>
                      <div className="font-heading font-bold text-sm text-slate-900">{PLAN_LABELS[p.id]?.label || p.id}</div>
                      <div className="text-[10px] text-slate-500 mb-1">{PLAN_LABELS[p.id]?.desc}</div>
                      <div className="text-xs font-mono">
                        {plans.free_beta ? (
                          <><span className="line-through text-slate-400">{p.price_eur.toFixed(2)}€</span> <span className="text-emerald-700 font-bold">Gratuit</span></>
                        ) : (
                          <span className="font-bold">{p.price_eur.toFixed(2)}€</span>
                        )}
                      </div>
                    </button>
                  ))}
                </div>
              </div>

              {error && (
                <div className="flex items-start gap-2 p-3 rounded-xl bg-rose-50 border border-rose-200 text-rose-700 text-xs">
                  <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
                  <span>{error}</span>
                </div>
              )}

              <button type="submit" disabled={submitting}
                data-testid="sponsor-submit-btn"
                className="btn-primary inline-flex items-center gap-2 disabled:opacity-50">
                {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                {submitting ? "Publication…" : "Publier l'annonce"}
              </button>
            </form>
          </div>
        </section>
      )}

      {/* My listings */}
      {user?.email && mine.length > 0 && (
        <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-10">
          <h2 className="font-heading font-bold text-xl mb-4 text-slate-900">Mes annonces</h2>
          <div className="grid sm:grid-cols-2 gap-3">
            {mine.map((s) => (
              <div key={s.id} className="border border-slate-200 rounded-2xl p-4 flex gap-3" data-testid={`my-sponsor-${s.id}`}>
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2 mb-1">
                    <StatusBadge status={s.status} />
                    <span className="text-[10px] text-slate-400">{PLAN_LABELS[s.plan]?.label}</span>
                  </div>
                  <p className="text-sm font-heading font-bold text-slate-900 truncate">{s.title}</p>
                  <div className="text-[11px] text-slate-500 flex items-center gap-3 mt-1">
                    <span>👁 {s.views}</span>
                    <span>↗ {s.clicks}</span>
                    {s.active_until && <span>Expire {fmtDate(s.active_until)}</span>}
                  </div>
                </div>
                <button onClick={() => deleteMine(s.id)} className="p-2 rounded-xl text-slate-400 hover:text-rose-600 hover:bg-rose-50 transition shrink-0" data-testid={`my-sponsor-delete-${s.id}`}>
                  <Trash2 className="w-4 h-4" />
                </button>
              </div>
            ))}
          </div>
        </section>
      )}

      {/* Public active listings */}
      <section className="max-w-5xl mx-auto px-6 lg:px-10 pb-20">
        <h2 className="font-heading font-bold text-xl mb-4 text-slate-900 flex items-center gap-2">
          <ShoppingBag className="w-5 h-5 text-[#E11D48]" />
          Annonces en cours
          <span className="text-xs font-mono text-slate-400">({active.length})</span>
        </h2>

        {loading ? (
          <div className="p-12 text-center text-sm text-slate-400">Chargement…</div>
        ) : active.length === 0 ? (
          <div className="border border-dashed border-slate-200 rounded-2xl p-12 text-center" data-testid="sponsors-empty">
            <Megaphone className="w-8 h-8 text-slate-300 mx-auto mb-3" />
            <p className="text-sm text-slate-500">Aucune annonce pour le moment.</p>
            {user?.email && (
              <button onClick={() => setShowForm(true)} className="mt-4 btn-primary text-xs">
                Soyez le premier à publier
              </button>
            )}
            {!user?.email && (
              <Link to="/login" className="mt-4 inline-block btn-primary text-xs">Se connecter pour publier</Link>
            )}
          </div>
        ) : (
          <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {active.map((s) => (
              <a
                key={s.id}
                href={s.url}
                onClick={() => handleClick(s)}
                target="_blank"
                rel="noopener noreferrer sponsored"
                data-testid={`sponsor-card-${s.id}`}
                className="group border border-slate-200 rounded-2xl overflow-hidden hover:border-[#E11D48] hover:shadow-md transition flex flex-col"
              >
                {s.image_url && (
                  <div className="aspect-[4/3] bg-slate-50 overflow-hidden">
                    <img src={s.image_url} alt={s.title} className="w-full h-full object-cover group-hover:scale-105 transition" loading="lazy" />
                  </div>
                )}
                <div className="p-4 flex-1 flex flex-col">
                  <h3 className="font-heading font-bold text-sm text-slate-900 group-hover:text-[#E11D48] transition line-clamp-2">{s.title}</h3>
                  {s.description && <p className="text-xs text-slate-500 mt-1 line-clamp-2">{s.description}</p>}
                  <div className="mt-auto pt-3 flex items-center justify-between gap-2">
                    {s.price_eur != null && (
                      <span className="font-mono font-bold text-sm text-slate-900">{Number(s.price_eur).toFixed(2)} €</span>
                    )}
                    <span className="inline-flex items-center gap-1 text-[10px] text-slate-500">
                      par {s.user_name} <ExternalLink className="w-3 h-3" />
                    </span>
                  </div>
                </div>
              </a>
            ))}
          </div>
        )}

        <p className="text-[10px] text-slate-400 mt-6 max-w-2xl">
          ⚠️ Cartonomie n'est pas partie à la transaction. Les annonces redirigent vers des plateformes tierces (Vinted, Cardmarket, eBay…).
          Vérifiez toujours le vendeur et utilisez les protections acheteur de la plateforme cible.
        </p>
      </section>
    </div>
  );
}
